/* THE CONVERSATION ON A POST, KEPT (supabase/0035, her call 2026-10-06).
 *
 * Until now the chat lived in the screen and died when she left the post: a
 * fact she told Brill that never reached the post was gone, nothing could be
 * undone after a refresh, and nobody could look back at what was said. Her
 * question was whether it would break the bank. It does not: SAVING is a few
 * kilobytes in her own Supabase; what costs money is what is SENT, and that is
 * `sendHistory` below — the same size as before.
 *
 * ONE ROW PER POST in its own table, never a column on social_posts: Momentum
 * loads every post in full when it opens, and a column that grows with every
 * turn would make that load grow with every conversation she has ever had.
 *
 * A turn is { role: "user" | "assistant" | "note", content, at } and, when
 * Brill's reply REWROTE the post, { before, after, beforeDraft } — the post
 * just before it landed and what landed, so Undo and "Go back to this" work
 * after a refresh. "note" turns are hers to read ("You undid that change")
 * and are never sent to Brill.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSupabase } from "../supabaseClient";

export const KEEP_TURNS = 60;       // the stored conversation, newest kept
export const KEEP_VERSIONS = 12;    // rewrites that keep their before / after
export const SEND_BRILL_REPLIES = 4; // Brill's own replies sent back to it
export const SEND_HER_TURNS = 30;   // her messages sent — short, and where her facts are

const now = () => new Date().toISOString();

export function userTurn(content) { return { role: "user", content: String(content || ""), at: now() }; }
export function noteTurn(content) { return { role: "note", content: String(content || ""), at: now() }; }
/** Brill's reply. Pass `rewrite` = { before, after, beforeDraft } when it changed the post. */
export function brillTurn(content, rewrite = null) {
  const t = { role: "assistant", content: String(content || ""), at: now() };
  if (rewrite && typeof rewrite.after === "string") {
    t.before = String(rewrite.before ?? "");
    t.after = rewrite.after;
    t.beforeDraft = rewrite.beforeDraft ?? null;
  }
  return t;
}

export const isRewrite = (t) => !!t && t.role === "assistant" && typeof t.after === "string";

/** Bounded for storage: the newest KEEP_TURNS, and only the newest
 *  KEEP_VERSIONS rewrites keep their copies of the post. */
export function trimTurns(turns = []) {
  const list = (turns || []).filter(Boolean).slice(-KEEP_TURNS);
  let kept = 0;
  for (let i = list.length - 1; i >= 0; i--) {
    if (!isRewrite(list[i])) continue;
    kept += 1;
    if (kept > KEEP_VERSIONS) {
      const { before, after, beforeDraft, ...rest } = list[i];
      list[i] = rest;
    }
  }
  return list;
}

/** WHAT GOES TO BRILL, and the reason this does not cost more than before:
 *  every message SHE typed (short, and where her facts live — HER_STORY_RULE),
 *  only Brill's last few replies (mostly "here's what I changed"), never a
 *  note, and never a copy of the post — the post rides in the [DRAFT] block. */
export function sendHistory(turns = []) {
  const list = (turns || []).filter((t) => t && (t.role === "user" || t.role === "assistant") && String(t.content || "").trim());
  const herIdx = list.map((t, i) => (t.role === "user" ? i : -1)).filter((i) => i > -1).slice(-SEND_HER_TURNS);
  const brillIdx = list.map((t, i) => (t.role === "assistant" ? i : -1)).filter((i) => i > -1).slice(-SEND_BRILL_REPLIES);
  const keep = new Set([...herIdx, ...brillIdx]);
  return list.filter((_, i) => keep.has(i)).map((t) => ({ role: t.role, content: t.content }));
}

/** The latest rewrite, and whether Undo still applies: only while the post is
 *  exactly what that rewrite left — once she has typed over it, Undo would
 *  throw her typing away. */
export function lastRewrite(turns = [], caption = "") {
  for (let i = (turns || []).length - 1; i >= 0; i--) {
    const t = turns[i];
    if (t?.role === "note" && /undid/i.test(t.content || "")) return null;
    if (isRewrite(t)) return { turn: t, index: i, canUndo: t.after === caption };
  }
  return null;
}

/** Versions in order, for the conversation sheet: each rewrite is one. */
export function versionsOf(turns = []) {
  const out = [];
  (turns || []).forEach((t, i) => { if (isRewrite(t)) out.push({ index: i, n: out.length + 2, after: t.after }); });
  return out;
}

/** IS SHE ASKING, OR TELLING? (her call, 2026-10-06: a question is not an
 *  edit.) The prompt says so, and a replay showed the model still rewrote on
 *  "what would make the ending land better?" — so the screen holds the line
 *  too: a rewrite that answers a QUESTION waits in a Use this / Keep mine card
 *  instead of landing in her post. A request worded as a question ("can you
 *  make it shorter?") is a request, and lands. */
export function isQuestion(msg) {
  const m = String(msg || "").trim();
  if (!m) return false;
  if (/^(please\b|(can|could|will|would) you\b|make|cut|try|add|remove|drop|change|rewrite|redo|tighten|shorten|lengthen|fix|swap|use|put|keep|turn|go|yes|yeah|yep|ok|okay|sure|do it|do that)/i.test(m)) return false;
  return /\?\s*$/.test(m) || /^(what|which|how|why|is|are|does|do|did|should|would|could|was|were|has|have|any thoughts)\b/i.test(m);
}

/* ── Storage ─────────────────────────────────────────────────────────────── */

/** `useSocialChat(postId, { ready })` → { turns, loaded, saveProblem, append, replace, ref }.
 *  `ready` is false while the post itself is not saved yet (a chat row points
 *  at it): the turns wait in the screen and are written the moment it is.
 *  Loads once when the post opens. A failed save never blocks the chat — it
 *  carries on in the screen — but it is SAID, because "kept" and "not kept"
 *  look identical until she comes back and the conversation is gone. */
export function useSocialChat(postId, { ready = true } = {}) {
  /* A ref, as recordStore holds it: useSupabase hands back a new function on
     every render, and an effect keyed on it would reload on every keystroke. */
  const getSb = useSupabase();
  const sbRef = useRef(getSb);
  sbRef.current = getSb;
  const [turns, setTurns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saveProblem, setSaveProblem] = useState("");
  const latest = useRef([]);
  const readyRef = useRef(ready);
  readyRef.current = ready;

  useEffect(() => {
    let gone = false;
    latest.current = [];
    setTurns([]); setLoaded(false); setSaveProblem("");
    if (!postId) { setLoaded(true); return undefined; }
    (async () => {
      try {
        const sb = await sbRef.current();
        const { data, error } = await sb.from("social_post_chats").select("turns").eq("post_id", postId).maybeSingle();
        if (gone) return;
        if (error) setSaveProblem(missingTable(error) ? NOT_RUN : "This conversation couldn't be loaded.");
        const t = Array.isArray(data?.turns) ? data.turns : [];
        latest.current = t;
        setTurns(t);
      } catch {
        if (!gone) setSaveProblem("This conversation couldn't be loaded.");
      } finally {
        if (!gone) setLoaded(true);
      }
    })();
    return () => { gone = true; };
  }, [postId]);

  const persist = useCallback(async (next) => {
    if (!postId || !readyRef.current) return;
    try {
      const sb = await sbRef.current();
      const { error } = await sb.from("social_post_chats")
        .upsert({ post_id: postId, turns: next, updated_at: now() }, { onConflict: "post_id" });
      setSaveProblem(error ? (missingTable(error) ? NOT_RUN : "This conversation didn't save — it will be gone when you leave the post.") : "");
    } catch {
      setSaveProblem("This conversation didn't save — it will be gone when you leave the post.");
    }
  }, [postId]);

  /* The post just saved for the first time: write what was said before it did. */
  useEffect(() => {
    if (ready && latest.current.length) persist(latest.current);
  }, [ready, persist]);

  const replace = useCallback((next) => {
    const t = trimTurns(next);
    latest.current = t;
    setTurns(t);
    persist(t);
    return t;
  }, [persist]);
  const append = useCallback((more) => replace([...latest.current, ...(Array.isArray(more) ? more : [more])]), [replace]);

  return { turns, loaded, saveProblem, append, replace, ref: latest };
}

const NOT_RUN = "This conversation isn't being kept yet — the database change (0035) hasn't been run.";
const missingTable = (error) => /social_post_chats|does not exist|schema cache/i.test(error?.message || "");
